import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useListProducts,
  useArchiveProduct,
  useUnarchiveProduct,
  useDeleteProduct,
  type ProductListParams,
} from '../../features/products/api';
import { useListLocations } from '../../features/warehouses/api';
import { AuditLogModal } from '../../components/audit/AuditLogModal';
import SearchInput from '../../components/SearchInput/SearchInput';
import { Table } from '../../components/Table/Table';
import {
  TableFilterBar,
  type TableFilterDefinition,
} from '../../components/TableFilterBar/TableFilterBar';
import type { SortDirection } from '../../hooks/sorting/types';
import './products.scss';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import fallbackProductImage from '../../assets/defeo-logo.png';

export default function ProductsListPage() {
  const { data: warehouses = [] } = useListLocations();

  const [sp, setSp] = useSearchParams();

  const [q, setQ] = useState(sp.get('q') || '');
  const debouncedQ = useDebouncedSearch(q, 350);

  const [sortingKey, setSortingKey] = useState(sp.get('sortKey') || 'updatedAt');
  const [sortingOrder, setSortingOrder] = useState(sp.get('sortOrder') || 'desc');
  const [page, setPage] = useState(Number(sp.get('page') || 1));
  const [pageSize, setPageSize] = useState(Number(sp.get('pageSize') || 50));
  const [locationIds, setLocationIds] = useState<string[]>(
    (sp.get('locationId') || '').split(',').filter(Boolean)
  );
  const [loadStatus, setLoadStatus] = useState(sp.get('loadStatus') || 'all');
  const [problemFirst, setProblemFirst] = useState(sp.get('problemFirst') === 'true');
  const [archived, setArchived] = useState(sp.get('archived') || 'exclude');
  const [auditOpen, setAuditOpen] = useState(false);

  useEffect(() => {
    setSp(
      {
        q: debouncedQ,
        sortKey: sortingKey,
        sortOrder: sortingOrder,
        page: String(page),
        pageSize: String(pageSize),
        locationId: locationIds.join(','),
        loadStatus,
        problemFirst: String(problemFirst),
        archived,
      },
      { replace: true }
    );
  }, [
    debouncedQ,
    sortingKey,
    sortingOrder,
    page,
    pageSize,
    locationIds,
    loadStatus,
    problemFirst,
    archived,
    setSp,
  ]);

  /*
   * These four live in the URL, so they arrive as free strings while the API
   * describes them as unions. Narrowed here, at the single point of use, rather
   * than at each `useState` — typing the state would push the cast out to every
   * <select> handler instead.
   */
  const { data, isLoading, isFetching, refetch } = useListProducts({
    sortingKey: sortingKey as ProductListParams['sortingKey'],
    sortingOrder: sortingOrder as ProductListParams['sortingOrder'],
    searchQuery: debouncedQ,
    page,
    pageSize,
    locationId: locationIds.join(','),
    loadStatus: loadStatus as ProductListParams['loadStatus'],
    problemFirst,
    archived: archived as ProductListParams['archived'],
  });

  const products = data?.products ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;

  const warehouseMap = useMemo(
    () => Object.fromEntries(warehouses.map((w: any) => [w.id, w])),
    [warehouses]
  );

  const archive = useArchiveProduct();
  const unarchive = useUnarchiveProduct();
  const del = useDeleteProduct();

  // Clicking a header sorts by it; re-clicking the active column flips direction.
  function handleSort(columnId: string) {
    if (sortingKey === columnId) {
      setSortingOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortingKey(columnId);
      setSortingOrder(columnId === 'updatedAt' ? 'desc' : 'asc');
    }
    setPage(1);
  }

  function RowActions({ product }: { product: any }) {
    const isArchived = !!product.deletedAt;
    return (
      <div className="product-row-actions">
        <Link className="btn btn--ghost" to={`/products/${product.id}/edit`}>
          Edit
        </Link>
        {!isArchived ? (
          <button
            className="btn btn--ghost"
            onClick={() => archive.mutate(product.id, { onSuccess: () => refetch() })}
          >
            Archive
          </button>
        ) : (
          <>
            <button
              className="btn btn--ghost"
              onClick={() => unarchive.mutate(product.id, { onSuccess: () => refetch() })}
            >
              Unarchive
            </button>
            <button
              className="btn btn--danger"
              onClick={() => {
                const typed = window.prompt(`Type the product name to delete:\n\n${product.name}`);
                if (typed?.trim() === (product.name || '').trim()) {
                  del.mutate(product.id, { onSuccess: () => refetch() });
                }
              }}
            >
              Delete
            </button>
          </>
        )}
      </div>
    );
  }

  const tableFilters: TableFilterDefinition[] = [
    {
      id: 'warehouse',
      label: 'Warehouse',
      type: 'multiSelect',
      value: locationIds,
      onChange: (value) => {
        setLocationIds(value);
        setPage(1);
      },
      options: (warehouses as any[]).map((w) => ({
        value: w.id,
        label: w.name || `${w.line1}, ${w.city}, ${w.state}`,
      })),
    },
    {
      id: 'load-status',
      label: 'Fulfilment',
      type: 'singleSelect',
      value: loadStatus,
      defaultValue: 'all',
      onChange: (value) => {
        setLoadStatus(value);
        setPage(1);
      },
      options: [
        { value: 'all', label: 'All' },
        { value: 'ok', label: 'Has active loads' },
        { value: 'missing', label: 'Missing fulfilment methods' },
      ],
    },
    {
      id: 'archived',
      label: 'Show',
      type: 'singleSelect',
      value: archived,
      defaultValue: 'exclude',
      onChange: (value) => {
        setArchived(value);
        setPage(1);
      },
      options: [
        { value: 'exclude', label: 'Active only' },
        { value: 'include', label: 'Active + archived' },
        { value: 'only', label: 'Archived only' },
      ],
    },
  ];

  const columns = [
    {
      id: 'image',
      header: '',
      className: 'cell-image',
      sortable: false,
      resizable: false,
      defaultWidth: 64,
      render: (p: any) => (
        <img
          src={p.imageUrl?.trim() || fallbackProductImage}
          alt={p.name || 'Product'}
          className="product-thumb"
          onError={(e: any) => {
            e.currentTarget.src = fallbackProductImage;
          }}
        />
      ),
    },
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      sortKey: 'name',
      className: 'cell-name',
      defaultWidth: 320,
      render: (p: any) => (
        <>
          <div className="product-name">
            <Link to={`/products/${p.id}/edit`} className="link">
              {p.name}
            </Link>
            {p.deletedAt && <span className="badge badge--archived">Archived</span>}
          </div>
          <div className="product-description">{p.description || '—'}</div>
        </>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      sortable: true,
      sortKey: 'deliveryType',
      defaultWidth: 110,
      render: (p: any) => (p.deliveryType === 'PALLET' ? 'Pallet' : 'Bulk'),
    },
    {
      id: 'warehouses',
      header: 'Warehouses',
      defaultWidth: 120,
      render: (p: any) => {
        const names = new Set(
          (p.stocks ?? []).map(
            (s: any) => warehouseMap[s.locationId]?.name || warehouseMap[s.locationId]?.line1 || '—'
          )
        );
        return names.size || 0;
      },
    },
    {
      id: 'inventory',
      header: 'Inventory',
      defaultWidth: 160,
      render: (p: any) => {
        if (!p.trackInventory) return <span className="badge badge--info">Not tracked</span>;
        const totalQty = (p.stocks ?? []).reduce(
          (sum: number, s: any) => sum + (s.quantity || 0),
          0
        );
        const reserved = (p.stocks ?? []).reduce(
          (sum: number, s: any) => sum + (s.reservedQty || 0),
          0
        );
        return (
          <>
            {totalQty - reserved} <span className="muted">({totalQty} total)</span>
          </>
        );
      },
    },
    {
      id: 'updated',
      header: 'Updated',
      sortable: true,
      sortKey: 'updatedAt',
      defaultWidth: 180,
      render: (p: any) => new Date(p.updatedAt).toLocaleString(),
    },
    {
      id: 'actions',
      header: 'Actions',
      className: 'col-actions actions-cell ta-right',
      sortable: false,
      resizable: false,
      defaultWidth: 190,
      render: (p: any) => <RowActions product={p} />,
    },
  ];

  return (
    <div className="card">
      <div className="products-header">
        <div>
          <h2 className="card__title">Product Catalog</h2>
          <p className="card__subtitle">
            Showing {products.length} of {totalCount} total
          </p>
        </div>
        <div className="page-actions">
          <Link to="/products/new" className="btn btn--primary">
            Create Product
          </Link>
          <button className="btn btn--primary" onClick={() => setAuditOpen(true)}>
            Audit log
          </button>
        </div>
      </div>

      {/* Search + quick toggle */}
      <div className="products-toolbar">
        <div className="products-toolbar__search">
          <SearchInput
            placeholder="Name or description…"
            value={q}
            onValueChange={(value) => {
              setQ(value);
              setPage(1);
            }}
          />
        </div>

        <button
          type="button"
          className={problemFirst ? 'btn btn--primary' : 'btn btn--ghost'}
          onClick={() => {
            setProblemFirst((v) => !v);
            setPage(1);
          }}
          title="Show products missing fulfilment methods first"
        >
          {problemFirst ? '✓ Problems first' : 'Problems first'}
        </button>
      </div>

      {/* Filters */}
      <TableFilterBar filters={tableFilters} />

      {/* Table */}
      <Table
        columns={columns}
        data={products as any[]}
        isLoading={isLoading}
        emptyMessage="No products match your filters."
        className="products-table"
        sortBy={sortingKey}
        sortDirection={sortingOrder as SortDirection}
        onSort={handleSort}
        getRowClassName={(p: any) => (!p.hasActiveLoadOptions ? 'row--warning' : '')}
        tableId="org-products"
      />

      {/* Pagination */}
      <div className="products-pagination">
        <div className="form-control">
          <label>Page size</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="pagination-controls">
          <button
            className="btn btn--ghost"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Prev
          </button>
          <div className="pagination-label">
            Page {page} of {totalPages}
          </div>
          <button
            className="btn btn--ghost"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {isFetching && <div className="loading-indicator">Refreshing…</div>}

      <AuditLogModal open={auditOpen} onClose={() => setAuditOpen(false)} entityType="PRODUCT" />
    </div>
  );
}
